import * as React from "react";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { LSResultItemBookjacket } from "../search/ls/LSResultItem/LSResultItemBookjacket";
import { LSResultItemLayout } from "../search/ls/LSResultItem/LSResultItemLayout";
import { LoansInfoContainer } from "./LoansDueDate";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
  Tooltip,
} from "@mui/material";
import { LSResultItemTitle } from "../search/ls/LSResultItem/LSResultItemTitle";
import { LSResultItemAuthor } from "../search/ls/LSResultItem/LSResultItemAuthor";
import { RatingHighContrast } from "../../rate-and-review/RatingHighContrast";
import { LSSerialIssueInformation } from "./LSSerialIssueInformation";
import { LSResultItemReadingLevelDisplay } from "../search/ls/LSResultItem/LSResultItemReadingLevelDisplay";
import { GenreDisplay } from "./GenreDisplay";
import { LSResultItemSeries } from "../search/ls/LSResultItem/LSResultItemSeries";
import { useLanguage } from "../../Language";
import { EResourceService } from "@tlc/ls2pac/dto/ReturnLoan";
import { RateAndReview } from "../../rate-and-review/RateAndReview";
import { WriteYourReviewButton } from "../../rate-and-review/WriteYourReviewButton";
import { useLocationConfiguration } from "../../LocationConfiguration";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { DownloadNowButton } from "../resource/downloadable/DownloadNowButton";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { OverflowMenu } from "@tlc/ui/menu/OverflowMenu";
import { useMenuAnchor } from "@tlc/ui/menu/useMenuAnchor";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useLoansSelection } from "./LoansSelectionProvider";

export type NonStudentLoanItem = {
  isStudent: false;
  onRenewSelected: (selectedResourceIds: readonly number[]) => void;
  onReturnEResource: (loan: HostSystemItemView) => void;
};
export type StudentLoanItem = {
  isStudent: true;
  onRenewSelected?: never;
  onReturnEResource?: never;
};

export type LoanItemProps = {
  loan: HostSystemItemView;
} & (StudentLoanItem | NonStudentLoanItem);

export const LoanItem = ({
  loan,
  isStudent,
  onRenewSelected,
  onReturnEResource,
}: LoanItemProps) => {
  const { translate } = useLanguage();
  const { isSelected, handleItemChange } = useLoansSelection();
  const isMobile = useIsMobile();
  const menuAnchor = useMenuAnchor(`loan-item-${loan.id}-menu`);
  const { closeMenu } = menuAnchor;

  const { locationConfiguration } = useLocationConfiguration();
  const { allowRenewals } =
    locationConfiguration?.pacSettings?.userActionSettings;

  const { resource, title, author, enumeration, chronology } = loan;

  const renewIcon = <AutorenewIcon />;
  const returnIcon = (
    <KeyboardReturnIcon
      sx={{
        transform: "scaleX(-1)",
      }}
    />
  );

  // We only show the eResource renew button when its a mackin title (subject to change?) its renewable, and downloadable
  const showRenewDownloadable =
    loan.eresourceService === EResourceService.mackin && loan.renewable;

  // eResource: Return - text (secondary - Mackin, Overdrive, BT)
  const showCancelDownloadable = loan.loanCancelable && loan.downloadable;

  const showDownloadableOverflowMenu =
    (showRenewDownloadable || showCancelDownloadable) && isMobile;

  const downloadableActions = (
    <>
      {/* eResource: Download - contained (primary) */}
      {!loan.pushable && (
        <DownloadNowButton
          itemId={loan.eresourceId}
          service={loan.eresourceService}
        />
      )}

      {showDownloadableOverflowMenu ? (
        <OverflowMenu {...menuAnchor} iconOrientaion="vertical" outlined>
          {showRenewDownloadable && (
            <MenuItem
              onClick={() => {
                onRenewSelected([Number(loan.resource!.id)]);
                closeMenu();
              }}
            >
              <ListItemIcon>{renewIcon}</ListItemIcon>
              <ListItemText>{translate("renewEResource")}</ListItemText>
            </MenuItem>
          )}
          {showCancelDownloadable && (
            <MenuItem
              onClick={() => {
                onReturnEResource(loan);
                closeMenu();
              }}
            >
              <ListItemIcon>{returnIcon}</ListItemIcon>
              <ListItemText>{translate("returnEResource")}</ListItemText>
            </MenuItem>
          )}
        </OverflowMenu>
      ) : (
        <>
          {showRenewDownloadable && (
            <Button
              startIcon={renewIcon}
              variant="outlined"
              onClick={() => onRenewSelected([Number(loan.resource!.id)])}
            >
              {translate("renewEResource")}
            </Button>
          )}

          {showCancelDownloadable && (
            <Button
              startIcon={returnIcon}
              variant="outlined"
              onClick={() => onReturnEResource(loan)}
            >
              {translate("returnEResource")}
            </Button>
          )}
        </>
      )}
    </>
  );

  const isPhysicalItemRenewable =
    loan.renewable && loan.resource && allowRenewals;

  const physicalActions = isPhysicalItemRenewable && (
    <Button
      variant="contained"
      color="primary"
      startIcon={<AutorenewIcon />}
      onClick={() => onRenewSelected([Number(loan.resource!.id)])}
    >
      {translate("renewLoan")}
    </Button>
  );

  const actionsComponent = !isStudent && (
    <>{loan.downloadable ? downloadableActions : physicalActions}</>
  );

  const batchRenewDisabled =
    !loan.resource ||
    !loan.renewable ||
    (loan.downloadable && loan.eresourceService !== EResourceService.mackin);

  return (
    <LSResultItemLayout
      titleComponent={
        resource ? (
          <LSResultItemTitle resource={resource} />
        ) : (
          <Typography component="h3" variant="h6" flex={1}>
            {title}
          </Typography>
        )
      }
      bookjacketComponent={
        <LSResultItemBookjacket
          {...(loan.resource
            ? { resource: loan.resource }
            : { title: loan.title })}
        />
      }
      checkboxComponent={
        !isStudent && (
          <Tooltip
            title={
              batchRenewDisabled
                ? translate("batchRenewDisabledTooltip")
                : undefined
            }
          >
            {/* span wrapper is for focusable tooltip when disabled */}
            <span tabIndex={batchRenewDisabled ? 0 : undefined}>
              <Checkbox
                disabled={batchRenewDisabled}
                color="primary"
                checked={isSelected(loan)}
                onChange={(e) => handleItemChange(loan, e.target.checked)}
                slotProps={{
                  input: {
                    "aria-label": `Select ${title}`,
                  },
                }}
              />
            </span>
          </Tooltip>
        )
      }
      actionsComponent={actionsComponent}
      authorComponent={
        <LSResultItemAuthor shortAuthor={resource?.shortAuthor || author} />
      }
    >
      {/* Format label and rating */}
      {resource && (
        <Box display="flex" alignItems="center" gap={2}>
          {resource.format && (
            <Box>
              <Chip variant="outlined" label={resource.format} />
            </Box>
          )}

          {!isStudent ? (
            <>
              <RateAndReview material={resource} precision={0.5} />
              {!isMobile && <WriteYourReviewButton resource={resource} />}
            </>
          ) : (
            <RatingHighContrast
              value={resource.ratingInformation?.average}
              readOnly
              precision={0.5}
            />
          )}
        </Box>
      )}
      <LSResultItemSeries resource={resource} />
      <LSSerialIssueInformation
        chronology={chronology}
        enumeration={enumeration}
      ></LSSerialIssueInformation>
      <LSResultItemReadingLevelDisplay resource={resource} />
      <GenreDisplay item={loan} />
      <LoansInfoContainer loanItem={loan} isEResource={loan.downloadable} />
      {loan.pushable && (
        <Typography>{translate("eResourcePushableMessage")}</Typography>
      )}
    </LSResultItemLayout>
  );
};
